import { definePreset } from "@primeng/themes";
import Aura from "@primeng/themes/aura";
import { BrandConfig, BrandPreset } from "../brand-config";

export const rantalainenPreset: BrandPreset = definePreset(Aura, {
  name: "rantalainen",
  primitive: {
    rantalainen: {
      50: "#e6edf4",
      100: "#ccdbe9",
      200: "#99b7d3",
      300: "#6693bd",
      400: "#336fa7",
      500: "#00437a", // Rantalainen brand color
      600: "#003662",
      700: "#002849",
      800: "#001b31",
      900: "#000d18",
      950: "#00070c",
    },
  },
  semantic: {
    primary: {
      color: "{rantalainen.500}",
      contrastColor: "#ffffff",
      hoverColor: "{rantalainen.600}",
      activeColor: "{rantalainen.700}",
    },
    highlight: {
      background: "{rantalainen.500}",
      color: "#ffffff",
    },
    colorScheme: {
      light: {
        primary: {
          color: "{rantalainen.500}",
          contrastColor: "{surface.900}",
          hoverColor: "{rantalainen.600}",
          activeColor: "{rantalainen.700}",
        },
        highlight: {
          background: "{rantalainen.50}",
          focusBackground: "{rantalainen.100}",
          color: "{rantalainen.700}",
          focusColor: "{rantalainen.800}",
        },
      },
      dark: {
        primary: {
          color: "{rantalainen.400}",
          contrastColor: "{surface.900}",
          activeColor: "{rantalainen.200}",
          borderColor: "{rantalainen.400}",
          hoverColor: "{rantalainen.300}",
        },
        highlight: {
          background: "color-mix(in srgb, {rantalainen.400}, transparent 84%)",
          focusBackground: "color-mix(in srgb, {rantalainen.400}, transparent 76%)",
          color: "rgba(255,255,255,.87)",
          focusColor: "rgba(255,255,255,.87)",
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              color: "{surface.0}",
              borderColor: "{rantalainen.500}",
              hoverColor: "color-mix(in srgb, {surface.0}, transparent 10%)",
              activeColor: "color-mix(in srgb, {surface.0}, transparent 15%)",
            },
            outlined: {
              primary: {
                color: "{rantalainen.500}",
                borderColor: "{rantalainen.500}",
                hoverColor: "{rantalainen.600}",
                hoverBackground: "color-mix(in srgb, {rantalainen.500}, transparent 95%)",
                activeBackground: "color-mix(in srgb, {rantalainen.500}, transparent 90%)",
              },
              danger: {
                borderColor: "{red.500}",
              },
            },
            text: {
              primary: {
                color: "{rantalainen.500}",
                hoverBackground: "color-mix(in srgb, {rantalainen.500}, transparent 95%)",
              },
            },
          },
        },
        dark: {
          root: {
            primary: {
              color: "{surface.0}",
              borderColor: "{rantalainen.400}",
              hoverColor: "color-mix(in srgb, {surface.0}, transparent 10%)",
              hoverBackground: "color-mix(in srgb, {rantalainen.300}, {rantalainen.400} 50%)",
              hoverBorderColor: "color-mix(in srgb, {rantalainen.300}, {rantalainen.400} 50%)",
              activeBackground: "{rantalainen.300}",
              activeBorderColor: "{rantalainen.300}",
            },
            outlined: {
              primary: {
                color: "{rantalainen.400}",
                borderColor: "{rantalainen.400}",
                hoverColor: "{rantalainen.300}",
                hoverBackground: "color-mix(in srgb, {rantalainen.400}, transparent 85%)",
                activeBackground: "color-mix(in srgb, {rantalainen.400}, transparent 75%)",
              },
            },
            text: {
              primary: {
                color: "{rantalainen.400}",
                hoverColor: "{rantalainen.300}",
              },
            },
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "#cfcfcf",
            },
          },
        },
      },
    },
    radiobutton: {
      colorScheme: {
        light: {
          checked: {
            color: "#ffffff",
          },
          icon: {
            checked: {
              color: "#ffffff",
              hoverColor: "#e8e8e8",
            },
          },
        },
      },
    },
    datepicker: {
      date: {
        selectedColor: "{surface.0}",
      },
    },
  },
});

export const rantalainenBrand: BrandConfig = {
  brandId: "rantalainen",
  companyName: "Rantalainen",
  productName: "Connect",
  primeNgPreset: rantalainenPreset,
  pageBackground: { light: "#f1f5f9", dark: "#09090b" },
  internalCard: {
    light: { background: "var(--color-blue-50)", borderColor: "var(--color-blue-500)" },
    dark: {
      background: "color-mix(in srgb, var(--color-blue-950), transparent 50%)",
      borderColor: "var(--color-blue-800)",
    },
  },
  // Internal-only tag — the original "warn" look, via PrimeNG's warn tag tokens
  internalTag: {
    light: { background: "var(--p-tag-warn-background)", color: "var(--p-tag-warn-color)" },
    dark: { background: "var(--p-tag-warn-background)", color: "var(--p-tag-warn-color)" },
  },
  surfacePalette: {
    0: "#ffffff",
    50: "#fafafa",
    100: "#f4f4f5",
    200: "#e4e4e7",
    300: "#d4d4d8",
    400: "#a1a1aa",
    500: "#71717a",
    600: "#52525b",
    700: "#3f3f46",
    800: "#27272a",
    900: "#18181b",
    950: "#09090b",
  },
  logos: {
    horizontalLogoLightMode: "brands/rantalainen/logo-horizontal-light-mode.png",
    horizontalLogoDarkMode: "brands/rantalainen/logo-horizontal-dark-mode.png",
    iconLogoLightMode: "brands/rantalainen/logo-icon-light-mode.png",
    iconLogoDarkMode: "brands/rantalainen/logo-icon-dark-mode.png",
  },
  favicons: {
    ico: "brands/rantalainen/favicon.ico",
    png16: "brands/rantalainen/favicon-16x16.png",
    png32: "brands/rantalainen/favicon-32x32.png",
    appleTouchIcon: "brands/rantalainen/apple-touch-icon.png",
    androidChrome192: "brands/rantalainen/android-chrome-192x192.png",
    androidChrome512: "brands/rantalainen/android-chrome-512x512.png",
  },
  supportEmail: "tuki.connect@rantalainen.fi",
  legalEmail: "legal@rantalainen.fi",
  gdprUrls: {
    en: "https://www.rantalainen.fi/en/gdpr/",
    fi: "https://www.rantalainen.fi/gdpr/",
    sv: "https://www.rantalainen.fi/sv/gdpr/",
    no: "https://www.rantalainen.fi/en/gdpr/",
  },
};
